"use client";

import { SignInButton, useAuth } from "@clerk/nextjs";
import Autoplay from "embla-carousel-autoplay";
import Image, { StaticImageData } from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";

import FeaturedImage1 from "@/../public/png_files/FeaturedImage1.png";
import FeaturedImage2 from "@/../public/png_files/FeaturedImage2.png";
import FeaturedImage3 from "@/../public/png_files/FeaturedImage3.png";
import { css, cva } from "@/../styled-system/css";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/Carousel";

import ButtonSkeleton from "../Skeleton/ButtonSkeleton";
import Button from "../ui/Button";

const carouselItems = [
  {
    title: "FIND YOUR NEW FAVORITE STYLE",
    imageSrc: FeaturedImage1,
    variant: "full",
    key: "feature-slide-1",
    alt: "Featured Slide 1",
  },
  {
    title: "STEP INTO YOUR BEST STYLE",
    imageSrc: FeaturedImage2,
    variant: "half-left",
    key: "feature-slide-2",
    alt: "Featured Slide 2",
  },
  {
    title: "STYLED FROM THE SHOES UP",
    imageSrc: FeaturedImage3,
    variant: "half-right",
    key: "feature-slide-3",
    alt: "Featured Slide 3",
  },
];

const carouselItemRecipe = cva({
  base: {
    pl: "0",
    position: "relative",
    aspectRatio: "3 / 4",
    sm: {
      aspectRatio: "1.14 / 1",
    },
    md: {
      height: "900px",
    },
  },
  variants: {
    variant: {
      full: {},
      "half-left": {
        md: {
          display: "flex",
          flexDirection: "row",
        },
      },
      "half-right": {
        md: {
          display: "flex",
          flexDirection: "row-reverse",
        },
      },
    },
  },
});

const carouselImageWrapperRecipe = cva({
  base: {
    position: "relative",
    width: "100%",
    height: "100%",
  },
  variants: {
    variant: {
      full: {},
      half: {
        md: {
          width: "50%",
        },
      },
    },
  },
});

const carouselItemContentRecipe = cva({
  base: {
    display: "flex",
    flexDirection: "column",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    alignItems: "center",
    gap: "2",
    sm: {
      transform: "translateY(-90%)",
      alignItems: "flex-start",
      top: "90%",
      left: "80px",
      pr: "80px",
    },
  },
  variants: {
    variant: {
      full: {},
      "half-left": {
        md: {
          position: "relative",
          top: "auto",
          left: "auto",
          transform: "none",
          bottom: "unset",
          width: "50%",
          justifyContent: "center",
          pl: "10",
          pr: "80px",
        },
      },

      "half-right": {
        md: {
          position: "relative",
          top: "auto",
          left: "auto",
          transform: "none",
          bottom: "unset",
          width: "50%",
          justifyContent: "center",
          pr: "10",
          pl: "80px",
        },
      },
    },
  },
});

const carouselHeadingRecipe = cva({
  base: {
    textStyle: "bannerTextSm",
    color: "white",
    textAlign: "center",
    maxWidth: "750px",
    "@media(min-width: 24rem)": {
      textStyle: "bannerText",
    },
    sm: {
      textAlign: "left",
    },
    md: {
      textStyle: "bannerTextLg",
    },
  },
  variants: {
    variant: {
      full: {},
      half: {
        md: {
          color: "black",
        },
      },
    },
  },
});

const carouselDotContainerClass = css({
  display: "flex",
  justifyContent: "center",
  gap: "2.5",
  mt: "4",
  transition: "all 0.3s ease-in-out",
});

const carouselDotClass = css({
  width: "16px",
  height: "16px",
  rounded: "full",
  transition: "all 0.3s ease-in-out",
  backgroundColor: "brandLightGray/70",
  '&[data-state="active"]': {
    backgroundColor: "teal.500",
    width: "48px",
  },
});

function FeaturedCarousel() {
  const { isSignedIn, isLoaded } = useAuth();
  const [api, setApi] = useState<CarouselApi | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCurrentIndex(api.selectedScrollSnap());
    };

    onSelect(); // Run once on mount
    api.on("select", onSelect);

    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  return (
    <Carousel
      css={{ position: "relative", overflow: "hidden" }}
      setApi={setApi}
      opts={{
        loop: true,
      }}
      plugins={[
        Autoplay({
          delay: 5000,
          stopOnMouseEnter: true,
        }),
      ]}
    >
      <CarouselContent css={{ width: "100%", height: "100%", ml: "0" }}>
        {carouselItems.map((item) => (
          <SliderVariant
            key={item.key}
            title={item.title}
            alt={item.alt}
            imageSrc={item.imageSrc}
            variant={item.variant as "full" | "half-left" | "half-right"}
            isSignedIn={isSignedIn}
            isLoaded={isLoaded}
          />
        ))}
      </CarouselContent>
      <CarouselPrevious />
      <CarouselNext />
      <div className={carouselDotContainerClass}>
        {carouselItems.map((_, index) => (
          <button
            key={`dot-${index}`}
            onClick={() => api?.scrollTo(index)}
            data-state={index == currentIndex ? "active" : "inactive"}
            className={carouselDotClass}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </Carousel>
  );
}

interface SliderVariantProps {
  title: string;
  alt: string;
  imageSrc: StaticImageData;
  variant: "full" | "half-left" | "half-right";
  isSignedIn: boolean | undefined;
  isLoaded: boolean | undefined;
}

function SliderVariant({
  title,
  alt,
  imageSrc,
  variant,
  isSignedIn,
  isLoaded,
}: SliderVariantProps) {
  const isHalf = variant === "half-left" || variant === "half-right";

  return (
    <CarouselItem css={carouselItemRecipe.raw({ variant })}>
      <div
        className={carouselImageWrapperRecipe({
          variant: isHalf ? "half" : "full",
        })}
      >
        <Image
          unoptimized
          loading="lazy"
          src={imageSrc}
          width={0}
          height={0}
          className={css({
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
          })}
          alt={alt}
        />
      </div>
      <div className={carouselItemContentRecipe({ variant })}>
        <h2
          className={carouselHeadingRecipe({
            variant: isHalf ? "half" : "full",
          })}
        >
          {title}
        </h2>
        {!isLoaded ? (
          <ButtonSkeleton />
        ) : !isSignedIn ? (
          <SignInButton>
            <Button
              variant="primary"
              width="fit"
              css={{ backgroundColor: "sky.500" }}
            >
              Join Today
            </Button>
          </SignInButton>
        ) : (
          <Button
            variant="primary"
            width="fit"
            css={{ backgroundColor: "sky.500" }}
            asChild
          >
            <Link href="/digital-closet" prefetch>
              Visit Closet
            </Link>
          </Button>
        )}
      </div>
    </CarouselItem>
  );
}

export default FeaturedCarousel;
